import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Grid, Typography, TextField, Select, MenuItem, FormControl, Box, Button, FormHelperText, Stack, Checkbox, ListItemText } from '@mui/material';
import { Icon } from "@iconify/react";
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { initiativeCreateOrUpdate, uploadInitiativeImage, initiativeGetById } from '../initiative.service';
import { getAllcountryManagement } from '../../../countryManagement/countryManagement.service';
import { getIntegratorTypes } from '../../../settings/integratorRedirects/integratorRedirects.service';
import { appendAzureSasToken } from '../../../../../service/azure.utils';
import Loader from '../../../../components/loader';
import {
  createMenuProps,
  FieldLabel,
  formFieldSx,
  helperTextSx,
  placeholderTextSx,
} from '../../../../styles/formStyles';
import AlertToast from '../../../../components/alertToast';
import { primaryIconColor } from '../../../../styles/iconStyles';
import InlineAlert from '../../../../components/inlineAlert';
import { resultMessage } from '../../../../../service/apiResult';
import {
  createEditBackButtonSx,
  createEditCardSx,
  createEditFooterSx,
  createEditPageHeaderSx,
  createEditPageShellSx,
  createEditPageTitleSx,
  createEditPageSubtitleSx,
  createEditPrimaryButtonSx,
} from '../../../../styles/pageFormStyles';

const menuProps = createMenuProps({ sx: { width: 'auto' } });

const selectFieldSx = {
  ...formFieldSx,
  '& .MuiSelect-select.MuiSelect-outlined.MuiInputBase-input.MuiOutlinedInput-input': { color: '#1C1B1F' },
  '& .MuiInputBase-input::placeholder': {
    color: '#625B71',
    opacity: 1,
    fontSize: 16,
    lineHeight: '24px',
    letterSpacing: '0.5px',
    fontWeight: 400
  }
};

function InitiativeCreation() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEditMode = searchParams.get('type') === 'edit' && id;

  const [loading, setLoading] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [alert, setAlert] = useState("");
  const [responseMsg, setResponseMsg] = useState("");
  const [saveError, setSaveError] = useState("");
  const [countryData, setCountryData] = useState([]);
  // The integrator types (ecosystem categories) an Initiative can be limited to: [{ category, name }].
  const [integratorTypes, setIntegratorTypes] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [userId, setUserId] = useState("");
  const ALERT_DURATION = 2000;
  const alertClose = () => setAlert("");

  const initiativeSchema = z.object({
    name: z.string().min(1, { message: "Initiative name is required" }),
    description: z.string().min(1, { message: "Description is required" }),
    sector: z.string().min(1, { message: "Sector is required" }),
    countryIds: z.array(z.string()).min(1, { message: "At least one country is required" }),
    initiativeLink: z.string().optional(),
    allowedIntegratorCategories: z.array(z.string()),
  });

  const {
    handleSubmit,
    formState: { errors },
    control,
    watch,
    setValue,
  } = useForm({
    resolver: zodResolver(initiativeSchema),
    defaultValues: {
      name: "",
      description: "",
      sector: "",
      countryIds: [],
      initiativeLink: "",
      allowedIntegratorCategories: [],
    },
  });

  const onSubmit = async (data) => {
    try {
      const payload = {
        name: data.name,
        description: data.description,
        sector: data.sector,
        countryIds: data.countryIds,
        initiativeLink: data.initiativeLink,
        // Empty = every integrator type may take the Initiative.
        allowedIntegratorCategories: data.allowedIntegratorCategories,
        imageUrl: previewImage,
        createdBy: userId,
      };

      setLoading(true);
      const res = await initiativeCreateOrUpdate(payload, isEditMode, id);

      if (res.success) {
        const successMsg = isEditMode ? "Initiative updated successfully" : "Initiative created successfully";
        setResponseMsg(resultMessage(res, successMsg));
        setAlert("true");
        setOpenAlert(true);
        setLoading(false);
        setTimeout(() => {
          navigate("/initiative");
          alertClose();
        }, ALERT_DURATION);
      } else {
        // The save failed and the user is still sitting on unsaved input, so the reason
        // stays on the form until dismissed rather than being flashed and lost.
        setSaveError(resultMessage(res));
        setLoading(false);
      }
    } catch (error) {
      const errorMsg = isEditMode ? "Initiative update failed" : "Initiative creation failed";
      console.error(errorMsg + ":", error);
      setResponseMsg(errorMsg);
      setAlert("false");
      setOpenAlert(true);
      setLoading(false);
      setTimeout(() => alertClose(), ALERT_DURATION);
    }
  };

  const countriesData = useCallback(async () => {
    try {
      const pageSize = 100;
      let page = 0;
      let hasNextPage = true;
      const allCountries = [];

      while (hasNextPage) {
        const res = await getAllcountryManagement(page, pageSize);
        const countries = Array.isArray(res?.data?.content) ? res.data.content : [];
        allCountries.push(...countries);

        const totalPages = Number(res?.data?.totalPages || 0);
        if (totalPages > 0) {
          hasNextPage = page + 1 < totalPages;
        } else {
          hasNextPage = countries.length === pageSize;
        }

        page += 1;
        if (page > 100) {
          hasNextPage = false;
        }
      }

      const uniqueCountries = Array.from(
        new Map(allCountries.map((country) => [country?.id ?? country?.name, country])).values()
      );

      const activeCountries = uniqueCountries.filter(
        (country) => (country?.status || '').toUpperCase() === 'ACTIVE'
      );
      setCountryData(activeCountries);
    } catch {
      setCountryData([]);
    }
  }, []);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const imageTypes = ["image/jpeg", "image/png", "image/jpg", "image/webp"];
    if (!imageTypes.includes(file.type)) {
      setResponseMsg("Only JPEG, PNG, WebP and JPG files are allowed.");
      setAlert("false");
      setOpenAlert(true);
      setTimeout(() => alertClose(), ALERT_DURATION);
      e.target.value = "";
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setResponseMsg("File size must be less than 10MB");
      setAlert("false");
      setOpenAlert(true);
      setTimeout(() => alertClose(), ALERT_DURATION);
      e.target.value = "";
      return;
    }

    try {
      setLoading(true);
      const azureUrl = await uploadInitiativeImage(file);
      setPreviewImage(azureUrl);
      setSelectedFile(file);
      setLoading(false);
    } catch (error) {
      console.error("Image upload failed:", error);
      setResponseMsg("Image upload failed");
      setAlert("false");
      setOpenAlert(true);
      setLoading(false);
    }
    e.target.value = "";
  };

  const handleCancel = () => {
    navigate("/initiative");
  };

  useEffect(() => {
    getIntegratorTypes().then((result) => {
      if (result.success) {
        setIntegratorTypes((result.data || []).map((type) => ({ category: type.category, name: type.name })));
      }
    });
  }, []);

  useEffect(() => {
    countriesData();
    // Get user ID from stored userData
    try {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      if (userData?.id) {
        setUserId(userData.id);
      }
    } catch (error) {
      console.error('Failed to retrieve user ID:', error);
    }
  }, [countriesData]);

  useEffect(() => {
    if (isEditMode && id) {
      const fetchInitiativeData = async () => {
        try {
          setLoading(true);
          const data = await initiativeGetById(id);
          if (data && !data.error) {
            const countryIds = Array.isArray(data.adminData?.countries)
              ? data.adminData.countries.map((c) => c.id).filter(Boolean)
              : [];

            setValue('name', data.name || '');
            setValue('description', data.description || '');
            setValue('sector', data.adminData?.sector || '');
            setValue('countryIds', countryIds);
            setValue('initiativeLink', data.adminData?.initiativeLink || '');
            setValue('allowedIntegratorCategories', Array.isArray(data.allowedIntegratorCategories)
              ? data.allowedIntegratorCategories
              : []);

            if (data.adminData?.imageUrl) {
              setPreviewImage(data.adminData.imageUrl);
            }
          }
          setLoading(false);
        } catch (error) {
          console.error('Failed to fetch initiative data:', error);
          setLoading(false);
        }
      };
      fetchInitiativeData();
    }
  }, [isEditMode, id, setValue]);

  return (
    <Box sx={createEditPageShellSx}>
      <Box sx={createEditPageHeaderSx}>
        <Button
          onClick={handleCancel}
          startIcon={<Icon icon="mdi:arrow-left" width="20" height="20" />}
          sx={createEditBackButtonSx}
        >
          Back
        </Button>
        <Grid container alignItems="center">
          <Grid size={12}>
            <Typography className="app-page-title" sx={createEditPageTitleSx}>
              {isEditMode ? 'Edit Initiative' : 'Create Admin Initiative'}
            </Typography>
            <Typography sx={createEditPageSubtitleSx}>Please fill in the fields below</Typography>
          </Grid>
        </Grid>
      </Box>
      <InlineAlert message={saveError} onClose={() => setSaveError("")} variant="error" />

      <form onSubmit={handleSubmit(onSubmit)} className="material-form">
        <Box sx={createEditCardSx}>
          <Grid container spacing={4} alignItems="stretch">
            <Grid size={{ xs: 12, lg: 6 }} sx={{ display: 'flex' }}>
              <Stack spacing={2.5} sx={{ width: '100%' }}>
                <Box>
                  <FieldLabel label="Initiative Name" required />
                  <Controller
                    name="name"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        placeholder="Enter initiative name"
                        fullWidth
                        error={!!error}
                        helperText={error ? error.message : ''}
                        sx={{ ...formFieldSx, ...helperTextSx }}
                      />
                    )}
                  />
                </Box>
                <Box>
                  <FieldLabel label="Sector" required />
                  <Controller
                    name="sector"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        placeholder="Enter sector"
                        fullWidth
                        error={!!error}
                        helperText={error ? error.message : ''}
                        sx={{ ...formFieldSx, ...helperTextSx }}
                      />
                    )}
                  />
                </Box>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }} sx={{ display: 'flex' }}>
              <Stack spacing={2.5} sx={{ width: '100%' }}>
                <Box>
                  <FieldLabel label="Country" required />
                  <FormControl fullWidth error={!!errors.countryIds} sx={helperTextSx}>
                    <Controller
                      name="countryIds"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          multiple
                          value={Array.isArray(field.value) ? field.value : []}
                          displayEmpty
                          MenuProps={menuProps}
                          IconComponent={(iconProps) => (
                            <Icon icon="mdi:chevron-down" width="22" height="22" color={primaryIconColor} {...iconProps} />
                          )}
                          sx={selectFieldSx}
                          renderValue={(selected) =>
                            !selected || selected.length === 0
                              ? <Box sx={placeholderTextSx}>Select country</Box>
                              : countryData.filter((item) => selected.includes(item.id)).map((item) => item.name).join(', ')
                          }
                        >
                          {(countryData || []).map((item) => (
                            <MenuItem key={item.id ?? item.name} value={item.id}>
                              <Checkbox checked={(Array.isArray(field.value) ? field.value : []).includes(item.id)} />
                              <ListItemText primary={item.name} />
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                    {!!errors.countryIds && (
                      <FormHelperText error>{errors.countryIds.message}</FormHelperText>
                    )}
                  </FormControl>
                </Box>
                <Box>
                  <FieldLabel label="Integrator Types Allowed" />
                  <FormControl fullWidth sx={helperTextSx}>
                    <Controller
                      name="allowedIntegratorCategories"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          multiple
                          value={Array.isArray(field.value) ? field.value : []}
                          displayEmpty
                          inputProps={{ 'aria-label': 'Integrator Types Allowed' }}
                          MenuProps={menuProps}
                          IconComponent={(iconProps) => (
                            <Icon icon="mdi:chevron-down" width="22" height="22" color={primaryIconColor} {...iconProps} />
                          )}
                          sx={selectFieldSx}
                          renderValue={(selected) =>
                            !selected || selected.length === 0
                              ? <Box sx={placeholderTextSx}>All integrator types</Box>
                              : integratorTypes.filter((type) => selected.includes(type.category)).map((type) => type.name).join(', ')
                          }
                        >
                          {integratorTypes.map((type) => (
                            <MenuItem key={type.category} value={type.category}>
                              <Checkbox checked={(Array.isArray(field.value) ? field.value : []).includes(type.category)} />
                              <ListItemText primary={type.name} />
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                    <FormHelperText>
                      Only integrators of these types can take this Initiative. Leave empty to allow every type.
                    </FormHelperText>
                  </FormControl>
                </Box>
                <Box>
                  <FieldLabel label="Initiative Link" />
                  <Controller
                    name="initiativeLink"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        placeholder="Enter initiative link"
                        fullWidth
                        error={!!error}
                        helperText={error ? error.message : ''}
                        sx={{ ...formFieldSx, ...helperTextSx }}
                      />
                    )}
                  />
                </Box>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box>
                <FieldLabel label="Description" required />
                <Controller
                  name="description"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      placeholder="Write description here..."
                      fullWidth
                      multiline
                      minRows={4}
                      error={!!error}
                      helperText={error ? error.message : ''}
                      sx={{ ...formFieldSx, ...helperTextSx }}
                    />
                  )}
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box>
                <FieldLabel label="Initiative Image" />
                <Box
                  sx={{
                    minHeight: 220,
                    border: '1px dashed #D9E2F4',
                    borderRadius: '12px',
                    backgroundColor: '#FFFFFF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    p: 3,
                    position: 'relative',
                    boxSizing: 'border-box',
                  }}
                >
                  {previewImage ? (
                    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Box
                        component="img"
                        src={appendAzureSasToken(previewImage)}
                        alt="Preview"
                        sx={{ maxWidth: '100%', maxHeight: 260, borderRadius: '12px' }}
                      />
                      <Button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedFile(null);
                          setPreviewImage(null);
                        }}
                        sx={{
                          minWidth: 'auto',
                          position: 'absolute',
                          top: -12,
                          right: -12,
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          bgcolor: '#FFFFFF',
                          border: '1px solid #D9E2F4',
                          color: '#DA342E',
                          p: 0,
                        }}
                      >
                        <Icon icon="mdi:close" width="18" height="18" />
                      </Button>
                    </Box>
                  ) : (
                    <Stack spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: '50%',
                          border: '2px solid #76A312',
                          color: '#76A312',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Icon icon="material-symbols:upload-rounded" width="32" height="32" />
                      </Box>
                      <Typography sx={{ fontSize: 14, lineHeight: '20px', letterSpacing: '0.1px', color: '#6C6A86' }}>
                        Supported formats: JPEG, PNG, WEBP (Max 10 MB)
                      </Typography>
                      <Button
                        onClick={() => document.getElementById('file-input')?.click()}
                        sx={{
                          minWidth: 180,
                          height: 40,
                          borderRadius: '10px',
                          border: '1px solid #76A312',
                          color: '#4F8506',
                          textTransform: 'none',
                          fontSize: 16,
                          lineHeight: '24px',
                          letterSpacing: '0.15px',
                          fontWeight: 500,
                        }}
                      >
                        Upload Image
                      </Button>
                    </Stack>
                  )}
                </Box>
                <input
                  hidden
                  type="file"
                  id="file-input"
                  accept=".jpeg,.jpg,.png,.webp"
                  onChange={handleFileChange}
                />
              </Box>
            </Grid>
          </Grid>
          <Box
            sx={{
              ...createEditFooterSx,
              flexDirection: { xs: 'column-reverse', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
            }}
          >
            <Button
              onClick={handleCancel}
              sx={{
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              sx={{ ...createEditPrimaryButtonSx, minWidth: { sm: 132 }, width: { xs: '100%', sm: 'auto' }, ml: { sm: 'auto' } }}
            >
              {isEditMode ? 'Update Initiative' : 'Create Initiative'}
            </Button>
          </Box>
        </Box>
      </form>
      {loading && <Loader />}
      <Box textAlign="end">
        <AlertToast open={openAlert && alert === 'true'} onClose={alertClose} message={responseMsg} variant="success" />
        <AlertToast open={openAlert && alert === 'false'} onClose={alertClose} message={responseMsg} variant="error" />
      </Box>
    </Box>
  );
}

export default InitiativeCreation;
